/**
 * @fileoverview Constants for Cold Room Ventilation Module
 * 
 * Physical constants, simulation parameters, and configuration values
 * for ventilation heat loss calculations and visualizations.
 * 
 * @module ColdRoomVentilation/constants
 */

// ============================================================================
// Simulation Step Constants
// ============================================================================

/**
 * Step identifier for introduction to ventilation concepts
 * @constant {string}
 */
export const FIRST_STEP = 'FIRST_STEP';

/**
 * Step identifier for ventilation heat loss formula
 * @constant {string}
 */
export const SECOND_STEP = 'SECOND_STEP';

/**
 * Step identifier for air change rate concepts
 * @constant {string}
 */
export const THIRD_STEP = 'THIRD_STEP';

/**
 * Step identifier for interactive ventilation calculator
 * @constant {string}
 */
export const FOURTH_STEP = 'FOURTH_STEP';

/**
 * Step identifier for heat pump impact analysis
 * @constant {string}
 */
export const FIFTH_STEP = 'FIFTH_STEP';

// ============================================================================
// Physical Constants
// ============================================================================

/**
 * Density of air at 20°C and 1 atm
 * @constant {number} kg/m³
 */
export const AIR_DENSITY = 1.2;

/**
 * Specific heat capacity of air at constant pressure
 * @constant {number} J/(kg·K) or kJ/(kg·K) × 1000
 */
export const AIR_SPECIFIC_HEAT = 1005; // J/(kg·K)

/**
 * Specific heat capacity of air (simplified)
 * @constant {number} Wh/(m³·K) - commonly used in building physics
 */
export const AIR_HEAT_CAPACITY = 0.34; // Wh/(m³·K)

// ============================================================================
// Default Room Parameters
// ============================================================================

/**
 * Default room dimensions
 */
export const DEFAULT_ROOM_LENGTH = 5; // meters
export const DEFAULT_ROOM_WIDTH = 4; // meters
export const DEFAULT_ROOM_HEIGHT = 2.5; // meters

/**
 * Calculate default room volume
 * @returns {number} Volume in m³
 */
export const DEFAULT_ROOM_VOLUME = DEFAULT_ROOM_LENGTH * DEFAULT_ROOM_WIDTH * DEFAULT_ROOM_HEIGHT;

/**
 * Default temperatures
 */
export const DEFAULT_INDOOR_TEMP = 20; // °C
export const DEFAULT_OUTDOOR_TEMP = -10; // °C (winter design condition)

/**
 * Default air change rate for residential buildings
 * @constant {number} 1/h (air changes per hour)
 */
export const DEFAULT_ACH = 0.5;

// ============================================================================
// Air Change Rates by Building Type
// ============================================================================

/**
 * Typical air change rates for different building types and conditions
 * @constant {Object}
 */
export const AIR_CHANGE_RATES = {
  // Residential buildings
  residential: {
    passive_house: { min: 0.1, max: 0.3, typical: 0.2, label: 'Passive House' },
    modern_sealed: { min: 0.3, max: 0.5, typical: 0.4, label: 'Modern Sealed Building' },
    standard_new: { min: 0.4, max: 0.7, typical: 0.5, label: 'Standard New Building' },
    older_building: { min: 0.6, max: 1.2, typical: 0.8, label: 'Older Building (pre-1990)' },
    leaky_old: { min: 1.0, max: 2.0, typical: 1.5, label: 'Leaky Old Building' },
  },
  // Ventilation modes
  ventilation: {
    windows_closed: { min: 0.1, max: 0.3, typical: 0.2, label: 'Windows Closed (Infiltration)' },
    tilt_ventilation: { min: 0.5, max: 1.5, typical: 1.0, label: 'Tilted Window' },
    window_open_partial: { min: 2, max: 5, typical: 3, label: 'Partially Open Window' },
    window_full_open: { min: 10, max: 20, typical: 15, label: 'Fully Open Window' },
    cross_ventilation: { min: 20, max: 40, typical: 30, label: 'Cross Ventilation' },
  },
  // Mechanical ventilation
  mechanical: {
    balanced_hrv: { min: 0.3, max: 0.5, typical: 0.4, label: 'Balanced with HRV (70-90% recovery)' },
    exhaust_only: { min: 0.3, max: 0.6, typical: 0.5, label: 'Exhaust Only System' },
    demand_controlled: { min: 0.1, max: 0.5, typical: 0.3, label: 'Demand Controlled (CO₂ sensor)' },
  },
};

// ============================================================================
// Heat Recovery Ventilation Parameters
// ============================================================================

/**
 * Heat recovery efficiency values
 * @constant {Object}
 */
export const HEAT_RECOVERY = {
  none: { efficiency: 0, label: 'No Heat Recovery' },
  basic: { efficiency: 0.5, label: 'Basic Plate Exchanger (50%)' },
  standard: { efficiency: 0.7, label: 'Standard HRV (70%)' },
  high_efficiency: { efficiency: 0.85, label: 'High Efficiency HRV (85%)' },
  rotary: { efficiency: 0.9, label: 'Rotary Heat Exchanger (90%)' },
};

// ============================================================================
// Animation Constants
// ============================================================================

/**
 * Animation timing constants
 */
export const ANIMATION_INTERVAL_MS = 50;
export const MAX_ANIMATION_VALUE = 100;
export const PARTICLE_COUNT = 40;

// ============================================================================
// Visualization Colors
// ============================================================================

export const COLORS = {
  warmAir: '#FF6B6B',
  coldAir: '#4DABF7',
  neutral: '#868E96',
  outdoor: '#228BE6',
  indoor: '#FA5252',
  arrow: '#0B75BD',
  ground: '#34A56F',
  wall: '#495057',
  window: '#74C0FC',
  text: {
    primary: '#212529',
    secondary: '#6C757D',
    gray: 'gray',
  },
};

// ============================================================================
// Formula Constants for LaTeX Rendering
// ============================================================================

export const FORMULAS = {
  ventilation_heat_loss: 'Q_{vent} = \\dot{V} \\cdot \\rho \\cdot c_p \\cdot \\Delta T',
  ventilation_simplified: 'Q_{vent} = 0.34 \\cdot V \\cdot n \\cdot \\Delta T',
  air_volume_flow: '\\dot{V} = V \\cdot n',
  temperature_difference: '\\Delta T = T_{inside} - T_{outside}',
  air_change_rate: 'n = \\frac{\\dot{V}}{V}',
  total_heat_loss: 'Q_{total} = Q_{transmission} + Q_{ventilation}',
  effective_ventilation: 'Q_{vent,eff} = Q_{vent} \\cdot (1 - \\eta_{HR})',
  cop_impact: 'P_{el} = \\frac{Q_{vent}}{COP}',
};

/**
 * Units for display
 */
export const UNITS = {
  heat_loss: 'W',
  volume: 'm³',
  volume_flow: 'm³/h',
  temperature: '°C',
  ach: '1/h',
  efficiency: '%',
  density: 'kg/m³',
  specific_heat: 'J/(kg·K)',
};
